/* Тело формы «Говорящего кружка» (APP-SPEC-4 §1.5): главная рамка (или рамка загрузки своего фото и видео, кадрирование),
   ряд лиц «Кто говорит», лента «Стиль анимации», поле «Что скажет?» (запись, своя речь), заметка, карточка «Ещё настройки».
   Переключение источника очищает противоположные входы до создания frozen intent, а не меняет уже принятый заказ. */
import type { ReactNode } from 'react';
import type { UserAvatar } from '../../../api/people';
import { uiText } from '../../../i18n';
import { PeoplePicker } from '../../shoot/kit/PeoplePicker';
import { artworkUrl } from '../studio-art-model';
import { StudioIcon } from '../StudioIcon';
import { cNum } from './order-tail-model';
import { toolField } from './tool-form';
import type { CircleFormState } from './CircleFieldInput';
import { CircleCropper, CircleHero, CirclePicture, CircleTrim } from './CircleHero';
import { CircleSettingsCard } from './CircleSettingsCard';
import { CircleAudioBox, CircleKeepCard, CircleNote, CircleTextBox } from './CircleSpeech';
import { CircleUpload } from './CircleUpload';
import { CircleViz } from './circle-viz';
import { circleCropAvailable, circleRatio } from './circle-model';
import { useCircleScreen, type CircleScreen, type CircleScreenOptions } from './use-circle-screen';

/** Бейдж цены за единицу на рамке (A1): только число сервера. */
function circleBadge(screen: CircleScreen): ReactNode {
  if (screen.unit === null || !(screen.unit > 0)) return null;
  return (
    <>
      <span className="mono">{cNum(screen.unit)}</span> C {uiText(screen.round ? 'за кружок' : 'за видео')}
    </>
  );
}

export function CircleBody({ screen, state, avatar }: { screen: CircleScreen; state: CircleFormState; avatar: UserAvatar | null }) {
  const { form, keys, who } = screen;
  const locked = state.locked;
  if (!form) return null;
  const badge = circleBadge(screen);
  const photoField = keys.photo ? toolField(form, keys.photo) : undefined;
  const videoField = keys.video ? toolField(form, keys.video) : undefined;
  const audioField = keys.audio ? toolField(form, keys.audio) : undefined;
  const ratio = circleRatio(screen.format);
  const canCrop = circleCropAvailable(form);
  const showPeople = screen.sources.char || screen.sources.me || screen.sources.person;
  const ownFaces = [
    ...(screen.sources.photo ? [{ value: 'own:photo', name: uiText('Фото'), icon: who === 'photo' ? 'photo' : 'plus', place: 'last' as const }] : []),
    ...(screen.sources.video ? [{ value: 'own:video', name: uiText('Видео'), icon: who === 'video' ? 'video' : 'plus', place: 'last' as const }] : []),
  ];
  const selected = who === 'photo' ? 'own:photo' : who === 'video' ? 'own:video' : screen.face;

  /* ── Главная рамка ── */
  let main: ReactNode;
  if (screen.cropping)
    main = (
      <CircleCropper
        format={screen.format}
        media={screen.cropping}
        value={screen.crop}
        onDone={value => screen.finishCrop(value)}
        onCancel={() => screen.finishCrop(null)}
      />
    );
  else if (who === 'photo' && photoField)
    main = (
      <CircleUpload
        field={photoField}
        state={state}
        round={screen.round}
        ratio={ratio}
        crop={screen.crop}
        canCrop={canCrop}
        badge={badge}
        onCrop={screen.startCrop}
        onMedia={value => screen.onMedia('photo', value)}
      />
    );
  else if (who === 'video' && videoField)
    main = (
      <CircleUpload
        field={videoField}
        state={state}
        round={screen.round}
        ratio={screen.round ? 1 : ratio}
        crop={screen.crop}
        canCrop={canCrop}
        badge={badge}
        onCrop={screen.startCrop}
        onMedia={value => screen.onMedia('video', value)}
      >
        {screen.trimWanted && screen.videoMedia && (
          <CircleTrim durationMs={screen.videoMedia.durationMs} maxMs={screen.maxMs} value={screen.trim} disabled={locked} onChange={screen.setTrim} />
        )}
      </CircleUpload>
    );
  else
    main = (
      <CircleHero
        format={screen.format}
        name={uiText(screen.hero.name)}
        sub={uiText(screen.hero.sub)}
        talking={screen.talking}
        empty={!who}
        badge={badge}
        picture={who === 'audio' ? <CircleViz style={screen.vizValue} live /> : <CirclePicture url={artworkUrl(screen.hero.image)} />}
      />
    );

  /* ── Что скажет ── */
  let speech: ReactNode = null;
  if (who && !screen.cropping) {
    if (screen.speech === 'own')
      speech = (
        <CircleKeepCard
          voiceName={screen.voice?.other && screen.voice.current !== 'src' ? screen.voice.other.name : null}
          durationMs={screen.ownMs}
          disabled={locked}
          onNewWords={screen.newWords.length ? () => screen.chooseSpeech(screen.newWords[0]) : undefined}
        />
      );
    else if (screen.speech === 'audio' && audioField)
      speech = (
        <CircleAudioBox
          field={audioField}
          state={state}
          audioOnly={who === 'audio'}
          onText={screen.speechChoices.includes('text') ? () => screen.chooseSpeech('text') : undefined}
          onDuration={screen.setAudioMs}
        />
      );
    else if (keys.text)
      speech = (
        <CircleTextBox
          value={screen.text}
          onChange={screen.setText}
          min={screen.bounds.min}
          max={screen.bounds.max}
          placeholder={screen.placeholder}
          disabled={locked}
          occasions={screen.occasions}
          occasion={screen.occasion}
          onOccasion={screen.pickOccasion}
          onMic={screen.speechChoices.includes('audio') ? () => screen.chooseSpeech('audio') : undefined}
          title={screen.title}
        />
      );
  }

  /* ── Карточка «Ещё настройки» ── */
  const oneRow = who === 'audio' && screen.speech === 'audio';
  const formatLocked = screen.preset.template ? 'Формат задаёт шаблон' : null;
  return (
    <>
      <div className="ap-sl" id="apSlMain">
        {main}
      </div>
      {showPeople && who !== 'audio' && (
        <div className="ap-sl" id="apSlWho">
          <PeoplePicker
            data={{
              ...screen.people,
              persons: screen.people.persons.map(person =>
                person.is_pet ? { ...person, selectable: false, disabled_reason: 'не скажет: нужен человек' } : person,
              ),
              characters: screen.sources.char ? screen.people.characters : [],
            }}
            avatar={avatar}
            mode="single"
            selected={selected ? [selected] : []}
            limit={1}
            locked={locked}
            onToggle={screen.pickFace}
            withCharacters={screen.sources.char}
            heading="Кто говорит"
            caption={
              screen.sources.photo || screen.sources.video
                ? `${uiText('Можно своё фото или видео')}${!screen.me.ready ? ` · ${uiText('Ваши фото: один раз для всех инструментов')}` : ''}`
                : 'Вы, близкий или персонаж'
            }
            extra={ownFaces}
          />
        </div>
      )}
      {!showPeople && who !== 'audio' && screen.sources.photo && screen.sources.video && (
        <div className="ap-sl circle-own" role="group" aria-label={uiText('Кто говорит')}>
          {ownFaces.map(item => (
            <button key={item.value} type="button" className={`chip${selected === item.value ? ' on' : ''}`} aria-pressed={selected === item.value} disabled={locked} onClick={() => screen.pickFace(item.value)}>
              {item.name}
            </button>
          ))}
        </div>
      )}
      {who === 'audio' && screen.vizChoices.length > 0 && (
        <div className="ap-sl" id="apSlSty">
          <div className="bcs circle-viz-rail">
            <div className="glbl bcs-gl">{uiText('Стиль анимации')}</div>
            <div className="bcs-r nosb" role="group" aria-label={uiText('Стиль анимации')}>
              {screen.vizChoices.map(([id, name]) => (
                <button
                  key={id}
                  type="button"
                  className={`bcs-c${screen.vizValue === id ? ' on' : ''}`}
                  aria-pressed={screen.vizValue === id}
                  disabled={locked}
                  onClick={() => screen.setViz(id)}
                >
                  <span className="pc">
                    <CircleViz style={id} className="tc-vz" />
                    <i className="ck" aria-hidden="true">
                      <StudioIcon kind="check" />
                    </i>
                  </span>
                  <b>{uiText(name)}</b>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {speech && (
        <div className="ap-sl" id="apSlTx">
          {speech}
        </div>
      )}
      {screen.note && !screen.cropping && (
        <div className="ap-sl" id="apSlNote">
          <CircleNote kind={screen.note.kind} text={screen.note.text} />
        </div>
      )}
      {who && !screen.cropping && (
        <div className="ap-sl" id="apSlSet">
          <CircleSettingsCard
            disabled={locked}
            oneRow={oneRow}
            openVoice={screen.voicePick}
            voice={oneRow ? null : screen.voice}
            format={
              keys.format && screen.formatChoices.length
                ? { choices: screen.formatChoices, current: screen.format, onPick: screen.setFormat, locked: formatLocked }
                : null
            }
            skin={screen.skins.length ? { skins: screen.skins, current: screen.skin, onPick: screen.setSkin } : null}
            loop={screen.loopOn ? { value: screen.loop, onChange: screen.setLoop } : null}
            motion={screen.motionOn ? { value: screen.motion, max: screen.motionMax, onChange: screen.setMotion } : null}
            sfx={screen.sfxOn ? { value: screen.sfx, max: screen.sfxMax, presets: screen.sfxPresets, onChange: screen.setSfx } : null}
          />
        </div>
      )}
    </>
  );
}

/** Совместимая точка входа: состояние экрана поверх общей формы и тело формы. */
export function CircleInputSections({
  state,
  avatar,
  onSpeechChoice,
  options,
}: {
  state: CircleFormState;
  avatar: UserAvatar | null;
  onSpeechChoice?(value: string | undefined): void;
  options?: CircleScreenOptions;
}) {
  const screen = useCircleScreen(state, avatar, { ...options, onSpeechChoice });
  return <CircleBody screen={screen} state={state} avatar={avatar} />;
}
